import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "../common/PageHeader";
import { getCookie } from "../../services/DataRequestService";
import { get_workflows } from "../../services/ApiManageWorkflow";
import resourcePlanningDummy from "./resourcePlanningDummy.json";

/* =============================================================================
   HELPERS
============================================================================= */

function formatDateCompact(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return String(isoString);
  const date = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

function formatShortDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatWeekLabel(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function formatNumber(value) {
  if (value == null || value === "") return "—";
  return Number(value).toLocaleString("en-IN");
}

function readLocalList(key) {
  try {
    const stored = JSON.parse(localStorage.getItem(key));
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

/* =============================================================================
   SELECTED WORKFLOW CARD
============================================================================= */

function PlanStatusCard({
  workflow,
  workflowOptions,
  isEditingWorkflow,
  draftWorkflowId,
  onStartEdit,
  onCancelEdit,
  onDraftChange,
  onApplyWorkflow,
}) {
  const processDate = workflow?.process_date;
  const workflowName = workflow?.workflow_name || workflow?.name || "N/A";

  return (
    <div className="scm-plan-status-card">
      <div className="scm-plan-status-row">
        <div className="scm-plan-status-copy">
          <h2 className="scm-plan-status-title">Selected Workflow</h2>
          {isEditingWorkflow ? (
            <select
              className="scm-filter-select scm-workflow-select"
              value={draftWorkflowId}
              onChange={(e) => onDraftChange(e.target.value)}
              aria-label="Change selected workflow"
            >
              {workflowOptions.map((item) => (
                <option key={item.workflow_id} value={item.workflow_id}>
                  {item.workflow_name}
                </option>
              ))}
            </select>
          ) : (
            <>
              <p className="scm-plan-status-workflow" title={workflowName}>
                {workflowName}
              </p>
              <p className="scm-plan-card-meta">
                Resource & Planning
                {workflow?.granularity ? ` · ${workflow.granularity}` : ""}
                {processDate ? ` · ${formatDateCompact(processDate)}` : ""}
              </p>
            </>
          )}
        </div>
        {!isEditingWorkflow ? (
          <button
            type="button"
            className="scm-config-btn scm-config-btn--secondary scm-plan-edit-btn"
            onClick={onStartEdit}
          >
            Edit
          </button>
        ) : (
          <div className="scm-workflow-edit-actions">
            <button
              type="button"
              className="scm-config-btn scm-config-btn--outline"
              onClick={onCancelEdit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="scm-config-btn scm-config-btn--primary"
              onClick={onApplyWorkflow}
            >
              Apply
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* =============================================================================
   RESOURCE AND PLANNING PANEL
============================================================================= */

const PARTS_PER_PAGE = 5;

function ResourcePlanningPanel({
  data = resourcePlanningDummy,
  onAskManagerApproval,
  approvalRequested,
  managerApproved,
}) {
  const clusters = data.clusters || [];
  const weeks = data.weeks || [];
  const [activeClusterId, setActiveClusterId] = useState(clusters[0]?.id || "");
  const [partsPage, setPartsPage] = useState(1);

  const activeCluster =
    clusters.find((c) => c.id === activeClusterId) || clusters[0] || null;

  const partsWithOrders = useMemo(() => {
    if (!activeCluster) return [];
    return (activeCluster.parts || []).filter((part) =>
      (part.order_quantities || []).some((q) => q != null && q !== 0)
    );
  }, [activeCluster]);

  const totalPartsPages = Math.max(
    1,
    Math.ceil(partsWithOrders.length / PARTS_PER_PAGE)
  );
  const safePartsPage = Math.min(partsPage, totalPartsPages);
  const pageStart = (safePartsPage - 1) * PARTS_PER_PAGE;
  const pagedParts = partsWithOrders.slice(pageStart, pageStart + PARTS_PER_PAGE);

  const selectCluster = (clusterId) => {
    setActiveClusterId(clusterId);
    setPartsPage(1);
  };

  if (!activeCluster) {
    return (
      <p className="rp-empty">
        Resource and planning data not available for this workflow.
      </p>
    );
  }

  let actionLabel = "Ask Approval for Manager";
  if (approvalRequested) actionLabel = "Approval Complete";
  else if (managerApproved) actionLabel = "Continue Vendor Approval";

  let statusText =
    "Ready to send all clusters, parts, and quantities for manager and vendor approval.";
  if (approvalRequested) {
    statusText = "Manager and vendor approval are complete for this request.";
  } else if (managerApproved) {
    statusText =
      "Manager approved. Open approval to complete vendor confirmation.";
  }

  const showingFrom = partsWithOrders.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PARTS_PER_PAGE, partsWithOrders.length);

  return (
    <div className="rp-panel">
      <section className="rp-card rp-card--overview">
        <div className="rp-sku-line">
          <span>SKU {data.sku}</span>
          <span>
            {clusters.length} clusters · {weeks.length} weeks ·{" "}
            {partsWithOrders.length} parts
          </span>
        </div>

        <div className="rp-cluster-tabs" role="tablist" aria-label="Clusters">
          {clusters.map((cluster) => (
            <button
              key={cluster.id}
              type="button"
              role="tab"
              aria-selected={cluster.id === activeCluster.id}
              className={`rp-cluster-tab${
                cluster.id === activeCluster.id ? " rp-cluster-tab--active" : ""
              }`}
              onClick={() => selectCluster(cluster.id)}
            >
              <span className="rp-cluster-tab-name">{cluster.name}</span>
              <span className="rp-cluster-tab-meta">
                {cluster.manufacture_lead_days ?? "—"}d lead
              </span>
            </button>
          ))}
        </div>

        <div className="rp-summary-grid">
          <div className="rp-nested">
            <div className="rp-card-head">
              <div>
                <h4>Forecast (Post Indentation)</h4>
                <p>Finished-goods demand by week for {activeCluster.name}</p>
              </div>
            </div>
            <div className="rp-mini-table-wrap">
              <table className="rp-table rp-table--summary">
                <thead>
                  <tr>
                    {weeks.map((week) => (
                      <th key={`fc-${week}`}>
                        <span className="rp-th-main">{formatWeekLabel(week)}</span>
                        <span className="rp-th-sub">Demand week</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {(activeCluster.forecast || []).map((value, index) => (
                      <td key={`fcv-${index}`}>{formatNumber(value)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="rp-nested">
            <div className="rp-card-head">
              <div>
                <h4>Manufacturing Start Date</h4>
                <p>
                  Start production {activeCluster.manufacture_lead_days} days
                  before each demand week
                </p>
              </div>
            </div>
            <div className="rp-mini-table-wrap">
              <table className="rp-table rp-table--summary">
                <thead>
                  <tr>
                    {weeks.map((week) => (
                      <th key={`ms-${week}`}>
                        <span className="rp-th-main">{formatWeekLabel(week)}</span>
                        <span className="rp-th-sub">For demand week</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {(activeCluster.manufacturing_start_dates || []).map(
                      (value, index) => (
                        <td key={`msv-${index}`}>
                          <span className="rp-mfg-date">
                            {formatShortDate(value)}
                          </span>
                          <span className="rp-mfg-label">Start make</span>
                        </td>
                      )
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="rp-card rp-card--parts">
        <div className="rp-card-head rp-card-head--parts">
          <div>
            <h4>Parts needed for manufacturing</h4>
            <p>
              {activeCluster.name} · order qty and order-by date for each demand
              week
            </p>
          </div>
          <span className="rp-parts-count">
            Showing {showingFrom}-{showingTo} of {partsWithOrders.length}
          </span>
        </div>

        <div className="rp-parts-scroll">
          <table className="rp-table rp-table--parts">
            <thead>
              <tr>
                <th className="rp-sticky-col">Part #</th>
                <th>Per unit</th>
                <th>Lead</th>
                <th>Stock</th>
                {weeks.map((week) => (
                  <th key={`week-h-${week}`} className="rp-week-head">
                    <span className="rp-th-main">{formatWeekLabel(week)}</span>
                    <span className="rp-th-sub">Demand week</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedParts.length === 0 ? (
                <tr>
                  <td colSpan={4 + weeks.length} className="rp-empty-cell">
                    No parts scheduled for this cluster.
                  </td>
                </tr>
              ) : (
                pagedParts.map((part) => (
                  <tr key={part.part}>
                    <td className="rp-sticky-col rp-part-cell">{part.part}</td>
                    <td>{formatNumber(part.qty_per_unit)}</td>
                    <td>
                      {part.delivery_lead_days != null
                        ? `${part.delivery_lead_days}d`
                        : "—"}
                    </td>
                    <td>{formatNumber(part.inventory_units)}</td>
                    {weeks.map((week, weekIndex) => (
                      <td key={`${part.part}-${week}`} className="rp-week-cell">
                        <span className="rp-cell-label">Order qty</span>
                        <span className="rp-qty">
                          {formatNumber(part.order_quantities?.[weekIndex])}
                        </span>
                        <span className="rp-cell-label">Order by</span>
                        <span className="rp-date">
                          {formatShortDate(part.order_dates?.[weekIndex])}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {partsWithOrders.length > PARTS_PER_PAGE && (
          <div className="rp-pagination">
            <button
              type="button"
              className="rp-page-btn"
              onClick={() => setPartsPage((p) => Math.max(1, p - 1))}
              disabled={safePartsPage <= 1}
            >
              Previous
            </button>
            <div className="rp-page-numbers">
              {Array.from({ length: totalPartsPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    type="button"
                    className={`rp-page-btn${
                      page === safePartsPage ? " rp-page-btn--active" : ""
                    }`}
                    onClick={() => setPartsPage(page)}
                  >
                    {page}
                  </button>
                )
              )}
            </div>
            <button
              type="button"
              className="rp-page-btn"
              onClick={() =>
                setPartsPage((p) => Math.min(totalPartsPages, p + 1))
              }
              disabled={safePartsPage >= totalPartsPages}
            >
              Next
            </button>
          </div>
        )}
      </section>

      <div className="rp-actions">
        <div className="rp-actions-copy">
          <p
            className={`rp-status${
              approvalRequested || managerApproved ? " rp-status--sent" : ""
            }`}
          >
            {statusText}
          </p>
        </div>
        <button
          type="button"
          className="rp-primary-btn"
          onClick={onAskManagerApproval}
          disabled={approvalRequested}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

/* =============================================================================
   APPROVAL POPUP (Manager from Management config, Vendor from Vendor config)
============================================================================= */

function ApprovalPopup({
  open,
  onClose,
  managers,
  vendors,
  selectedManager,
  setSelectedManager,
  selectedVendor,
  setSelectedVendor,
  approvedByManager,
  approvedVendor,
  onManagerApprove,
  onVendorApprove,
  popupNotice,
  setPopupNotice,
}) {
  if (!open) return null;

  const isManagerApproved = Boolean(approvedByManager);
  const isVendorApproved = Boolean(approvedVendor);

  return (
    <div
      className="scm-approve-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="scm-approve-modal" onClick={(e) => e.stopPropagation()}>
        <div className="scm-approve-modal-head">
          <div>
            <h3>Approval</h3>
            <p>Manager approves first, then vendor confirmation.</p>
          </div>
          <button
            type="button"
            className="scm-approve-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {popupNotice && (
          <div
            className={`scm-popup-notice scm-popup-notice--${popupNotice.type}`}
            role="status"
          >
            <div>
              <strong>{popupNotice.title}</strong>
              <p>{popupNotice.message}</p>
            </div>
            <button
              type="button"
              className="scm-popup-notice-close"
              onClick={() => setPopupNotice(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <div className="scm-approve-modal-body">
          <section className="scm-approve-section">
            <div className="scm-approve-section-head">
              <span className="scm-approve-step">1</span>
              <div>
                <h4>Management Approval</h4>
                <p>Select manager from Configuration → Management</p>
              </div>
            </div>

            <label htmlFor="scm-popup-manager" className="scm-filter-label">
              Manager
            </label>
            <select
              id="scm-popup-manager"
              className="scm-filter-select"
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              disabled={isManagerApproved}
            >
              <option value="">-- Select Manager --</option>
              {managers.length === 0 ? (
                <option value="" disabled>
                  No managers in config
                </option>
              ) : (
                managers.map((m) => (
                  <option key={m.mgmt_id || m.mail || m.name} value={m.name}>
                    {m.name}
                    {m.role ? ` (${m.role})` : ""}
                  </option>
                ))
              )}
            </select>

            {isManagerApproved ? (
              <div className="scm-approve-done">
                Approved by <strong>{approvedByManager}</strong>
              </div>
            ) : (
              <button
                type="button"
                className="scm-switch-btn scm-approve-section-btn"
                onClick={onManagerApprove}
              >
                Approve as Manager
              </button>
            )}
          </section>

          <section
            className={`scm-approve-section${
              isManagerApproved ? "" : " scm-approve-section--locked"
            }`}
          >
            <div className="scm-approve-section-head">
              <span className="scm-approve-step">2</span>
              <div>
                <h4>Vendor Approval</h4>
                <p>Select vendor from Configuration → Vendor</p>
              </div>
            </div>

            <label htmlFor="scm-popup-vendor" className="scm-filter-label">
              Vendor
            </label>
            <select
              id="scm-popup-vendor"
              className="scm-filter-select"
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              disabled={!isManagerApproved || isVendorApproved}
            >
              <option value="">-- Select Vendor --</option>
              {vendors.length === 0 ? (
                <option value="" disabled>
                  No vendors in config
                </option>
              ) : (
                vendors.map((v) => (
                  <option
                    key={v.vendor_id || v.email || v.vendor_name}
                    value={String(v.vendor_id || v.vendor_name)}
                  >
                    {v.vendor_name}
                    {v.poc ? ` · ${v.poc}` : ""}
                  </option>
                ))
              )}
            </select>

            {!isManagerApproved && (
              <p className="scm-approve-hint">
                Waiting for management approval before vendor can confirm.
              </p>
            )}

            {isVendorApproved ? (
              <div className="scm-approve-done">
                Sent to vendor <strong>{approvedVendor}</strong>
              </div>
            ) : (
              <button
                type="button"
                className="scm-approve-btn scm-approve-section-btn"
                disabled={!isManagerApproved}
                onClick={onVendorApprove}
                style={{
                  opacity: !isManagerApproved ? 0.55 : 1,
                  cursor: !isManagerApproved ? "not-allowed" : "pointer",
                }}
              >
                Approve as Vendor
              </button>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/* =============================================================================
   MAIN PAGE
============================================================================= */

export default function SupplyChainMain() {
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedWorkflow, setSelectedWorkflow] = useState(
    () => location.state?.workflow || null
  );
  const [workflowList, setWorkflowList] = useState([]);
  const [isEditingWorkflow, setIsEditingWorkflow] = useState(false);
  const [draftWorkflowId, setDraftWorkflowId] = useState("");

  const [managers, setManagers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showApprovePopup, setShowApprovePopup] = useState(false);
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [approvedByManager, setApprovedByManager] = useState("");
  const [approvedVendor, setApprovedVendor] = useState("");
  const [popupNotice, setPopupNotice] = useState(null);

  const workflow = selectedWorkflow;
  const workflowId = workflow?.workflow_id || workflow?.id || "WF001";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const incoming = location.state?.workflow;
    if (!incoming?.workflow_id) return;
    setSelectedWorkflow((prev) =>
      prev?.workflow_id === incoming.workflow_id ? prev : incoming
    );
  }, [location.state?.workflow]);

  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const data = (await get_workflows(getCookie("sub"))) || [];
        setWorkflowList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching workflows:", error.message);
        setWorkflowList([]);
      }
    };
    loadWorkflows();
  }, []);

  useEffect(() => {
    const mgmt = readLocalList("scm_management");
    setManagers(mgmt);
    if (mgmt.length > 0 && !selectedManager) {
      setSelectedManager(mgmt[0].name || "");
    }

    const vendorList = readLocalList("scm_vendors");
    setVendors(vendorList);
    if (vendorList.length > 0 && !selectedVendor) {
      setSelectedVendor(
        String(vendorList[0].vendor_id || vendorList[0].vendor_name || "")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showApprovePopup]);

  useEffect(() => {
    setIsEditingWorkflow(false);
    setDraftWorkflowId("");
    setShowApprovePopup(false);
    setApprovedByManager("");
    setApprovedVendor("");
    setPopupNotice(null);
  }, [workflowId]);

  const workflowOptions = useMemo(() => {
    const completed = workflowList.filter(
      (item) => item?.status?.toLowerCase() === "completed" && item?.workflow_id
    );
    if (
      workflow?.workflow_id &&
      !completed.some((item) => item.workflow_id === workflow.workflow_id)
    ) {
      return [workflow, ...completed];
    }
    return completed;
  }, [workflowList, workflow]);

  const handleStartEditWorkflow = () => {
    setDraftWorkflowId(String(workflow?.workflow_id || ""));
    setIsEditingWorkflow(true);
  };

  const handleCancelEditWorkflow = () => {
    setDraftWorkflowId("");
    setIsEditingWorkflow(false);
  };

  const handleApplyWorkflow = () => {
    const next =
      workflowOptions.find(
        (item) => String(item.workflow_id) === String(draftWorkflowId)
      ) || null;
    if (!next) {
      setIsEditingWorkflow(false);
      return;
    }
    setSelectedWorkflow(next);
    setIsEditingWorkflow(false);
    setDraftWorkflowId("");
    navigate("/SupplyChainManagement/detail", {
      replace: true,
      state: {
        ...(location.state || {}),
        workflow: next,
        initialStep: "inventory",
      },
    });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const closeApprovePopup = () => {
    setShowApprovePopup(false);
    setPopupNotice(null);
  };

  const handleManagerApprove = () => {
    if (managers.length === 0) {
      setPopupNotice({
        type: "warning",
        title: "No managers found",
        message: "Add them in Configuration → Management.",
      });
      return;
    }
    if (!selectedManager) {
      setPopupNotice({
        type: "warning",
        title: "Select a manager",
        message: "Choose a manager before approving.",
      });
      return;
    }
    setApprovedByManager(selectedManager);
    setPopupNotice({
      type: "success",
      title: "Management Approved",
      message: `Approved by ${selectedManager}. Vendor step is now unlocked.`,
    });
  };

  const handleVendorApprove = () => {
    if (!approvedByManager) {
      setPopupNotice({
        type: "warning",
        title: "Manager approval required",
        message: "Complete management approval first.",
      });
      return;
    }
    if (vendors.length === 0) {
      setPopupNotice({
        type: "warning",
        title: "No vendors found",
        message: "Add them in Configuration → Vendor.",
      });
      return;
    }
    if (!selectedVendor) {
      setPopupNotice({
        type: "warning",
        title: "Select a vendor",
        message: "Choose a vendor before approving.",
      });
      return;
    }
    const vendor =
      vendors.find(
        (v) => String(v.vendor_id || v.vendor_name) === String(selectedVendor)
      ) || null;
    const vendorName = vendor?.vendor_name || selectedVendor;
    setApprovedVendor(vendorName);
    setPopupNotice({
      type: "success",
      title: "Vendor Approved",
      message: `Requirement sent to ${vendorName}.`,
    });
  };

  return (
    <>
      <div className="scm-page-wrapper">
        <div className="scm-container">
          <button
            type="button"
            className="scm-back-btn"
            onClick={() => navigate("/SupplyChainManagement")}
          >
            ← Back to Supply Chain
          </button>

          <PageHeader title="Resource and Planning" />

          <PlanStatusCard
            workflow={workflow}
            workflowOptions={workflowOptions}
            isEditingWorkflow={isEditingWorkflow}
            draftWorkflowId={draftWorkflowId}
            onStartEdit={handleStartEditWorkflow}
            onCancelEdit={handleCancelEditWorkflow}
            onDraftChange={setDraftWorkflowId}
            onApplyWorkflow={handleApplyWorkflow}
          />

          {(approvedByManager || approvedVendor) && (
            <div className="scm-mgmt-banner" role="status">
              <span className="scm-mgmt-check">✓</span>
              <div>
                {approvedByManager ? (
                  <>
                    Approved by <strong>{approvedByManager}</strong>
                  </>
                ) : (
                  "Pending management approval"
                )}
                {approvedVendor
                  ? ` · Vendor: ${approvedVendor}`
                  : " · Pending vendor approval"}
              </div>
            </div>
          )}

          <ResourcePlanningPanel
            data={resourcePlanningDummy}
            onAskManagerApproval={() => setShowApprovePopup(true)}
            approvalRequested={Boolean(approvedByManager && approvedVendor)}
            managerApproved={Boolean(approvedByManager)}
          />
        </div>
      </div>

      <ApprovalPopup
        open={showApprovePopup}
        onClose={closeApprovePopup}
        managers={managers}
        vendors={vendors}
        selectedManager={selectedManager}
        setSelectedManager={setSelectedManager}
        selectedVendor={selectedVendor}
        setSelectedVendor={setSelectedVendor}
        approvedByManager={approvedByManager}
        approvedVendor={approvedVendor}
        onManagerApprove={handleManagerApprove}
        onVendorApprove={handleVendorApprove}
        popupNotice={popupNotice}
        setPopupNotice={setPopupNotice}
      />

      <style>{SCM_STYLES}</style>
    </>
  );
}

/* =============================================================================
   STYLES
============================================================================= */

const SCM_STYLES = `
  /* --- page shell --- */
  .scm-page-wrapper {
    width: 100%;
    background: transparent;
    font-family: Poppins, sans-serif;
    box-sizing: border-box;
  }
  .scm-container {
    width: 100%;
    margin: 0 auto;
    padding: 8px 4px 20px;
    background: transparent;
    box-sizing: border-box;
    color: #0c3c54;
  }
  .scm-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    color: #032B4E;
    background-color: #fff;
    border: 1px solid #e6eef4;
    border-radius: 8px;
    cursor: pointer;
    font-family: Poppins, sans-serif;
    box-shadow: 0 2px 8px rgba(12, 60, 84, 0.06);
  }
  .scm-back-btn:hover {
    background-color: #032B4E;
    color: #fff;
    border-color: #032B4E;
  }

  /* --- selected workflow card --- */
  .scm-plan-status-card {
    width: 100%;
    background-color: #fff;
    border: 1px solid #e6eef4;
    border-left: 4px solid #032B4E;
    border-radius: 12px;
    padding: 14px 18px;
    box-sizing: border-box;
    margin-bottom: 16px;
    box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
  }
  .scm-plan-status-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .scm-plan-status-copy { min-width: 0; flex: 1 1 240px; }
  .scm-plan-status-title {
    font-size: 12px;
    font-weight: 600;
    color: #6a7d8c;
    margin: 0 0 4px;
    font-family: Poppins, sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .scm-plan-status-workflow {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: #0c3c54;
    font-family: Poppins, sans-serif;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .scm-plan-edit-btn {
    align-self: center;
    margin-left: auto;
  }
  .scm-plan-card-meta {
    font-size: 12px;
    color: #6a7d8c;
    margin: 4px 0 0;
    font-family: Poppins, sans-serif;
  }
  .scm-workflow-select { max-width: 420px; margin-top: 4px; }
  .scm-workflow-edit-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    align-items: center;
    margin-left: auto;
  }
  .scm-filter-label {
    display: block;
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #0c3c54;
    font-family: Poppins, sans-serif;
  }
  .scm-filter-select {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid #d9e4ee;
    border-radius: 8px;
    font-family: Poppins, sans-serif;
    font-size: 13px;
    color: #0c3c54;
    background: #fff;
    box-sizing: border-box;
  }
  .scm-config-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 36px;
    padding: 8px 16px;
    font-size: 12px;
    font-weight: 700;
    font-family: Poppins, sans-serif;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 1.2;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  }
  .scm-config-btn--primary {
    color: #fff;
    background: #E06639;
    border: none;
    box-shadow: 0 2px 8px rgba(224, 102, 57, 0.28);
  }
  .scm-config-btn--primary:hover {
    background: #c9552e;
  }
  .scm-config-btn--secondary {
    color: #fff;
    background: #032B4E;
    border: none;
    box-shadow: 0 2px 8px rgba(3, 43, 78, 0.22);
  }
  .scm-config-btn--secondary:hover {
    background: #021a30;
  }
  .scm-config-btn--outline {
    color: #032B4E;
    background: #fff;
    border: 1px solid #e6eef4;
    box-shadow: 0 2px 6px rgba(12, 60, 84, 0.06);
  }
  .scm-config-btn--outline:hover {
    border-color: #032B4E;
    background: #f6fafd;
  }

  /* --- approval status banner --- */
  .scm-mgmt-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding: 10px 14px;
    background: #eef8f2;
    border: 1px solid #c9e8d5;
    border-radius: 10px;
    color: #1f5b3a;
    font-size: 13px;
    font-family: Poppins, sans-serif;
  }
  .scm-mgmt-check {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #368E60;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  /* --- approval popup --- */
  .scm-approve-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(2, 26, 48, 0.52);
    backdrop-filter: blur(4px);
    padding: 16px;
  }
  .scm-approve-modal {
    width: min(520px, 100%);
    background: #fff;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 18px 48px rgba(2, 26, 48, 0.28);
  }
  .scm-approve-modal-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    background: #032B4E;
    color: #fff;
  }
  .scm-approve-modal-head h3 {
    margin: 0;
    font-size: 18px;
    font-family: Poppins, sans-serif;
  }
  .scm-approve-modal-head p {
    margin: 4px 0 0;
    font-size: 12px;
    opacity: 0.85;
    font-family: Poppins, sans-serif;
  }
  .scm-approve-close {
    border: none;
    background: transparent;
    color: #fff;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
  }
  .scm-approve-modal-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .scm-approve-section {
    border: 1px solid #e6eef4;
    border-radius: 12px;
    padding: 14px;
    background: #fff;
  }
  .scm-approve-section--locked { opacity: 0.72; background: #f8fafc; }
  .scm-approve-section-head {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 12px;
  }
  .scm-approve-step {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #032B4E;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .scm-approve-section--locked .scm-approve-step { background: #9aa8b5; }
  .scm-approve-section-head h4 {
    margin: 0;
    font-size: 14px;
    color: #0c3c54;
    font-family: Poppins, sans-serif;
  }
  .scm-approve-section-head p {
    margin: 2px 0 0;
    font-size: 12px;
    color: #6a7d8c;
    font-family: Poppins, sans-serif;
  }
  .scm-approve-section-btn {
    width: 100%;
    margin-top: 12px;
    height: 36px;
  }
  .scm-switch-btn {
    font-family: Poppins, sans-serif;
    background: #032B4E;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 0 18px;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
  }
  .scm-switch-btn:hover { background: #021a30; }
  .scm-approve-btn {
    font-family: Poppins, sans-serif;
    background: #E06639;
    color: #fff;
    border: none;
    border-radius: 6px;
    padding: 0 18px;
    font-weight: 600;
    font-size: 12px;
    cursor: pointer;
  }
  .scm-approve-done {
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #eef8f2;
    color: #1f5b3a;
    font-size: 13px;
    font-family: Poppins, sans-serif;
  }
  .scm-approve-hint {
    margin: 10px 0 0;
    font-size: 12px;
    color: #6a7d8c;
    font-family: Poppins, sans-serif;
  }
  .scm-popup-notice {
    margin: 12px 16px 0;
    padding: 10px 12px;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-family: Poppins, sans-serif;
  }
  .scm-popup-notice--success { background: #eef8f2; color: #1f5b3a; }
  .scm-popup-notice--warning { background: #fff7ed; color: #9a3412; }
  .scm-popup-notice p { margin: 2px 0 0; font-size: 12px; }
  .scm-popup-notice-close {
    border: none;
    background: transparent;
    font-size: 18px;
    cursor: pointer;
    color: inherit;
  }

  /* --- resource and planning --- */
  .rp-panel { display: flex; flex-direction: column; gap: 14px; }
  .rp-card--overview {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 14px 16px 16px;
    overflow: hidden;
    box-sizing: border-box;
  }
  .rp-sku-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    font-family: Poppins, sans-serif;
    font-size: 13px;
    color: #6a7d8c;
  }
  .rp-sku-line span:first-child {
    font-weight: 700;
    color: #0c3c54;
    font-size: 15px;
  }
  .rp-cluster-tabs {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 4px;
    flex-wrap: nowrap;
    align-items: stretch;
  }
  .rp-cluster-tab {
    flex: 0 0 auto;
    min-width: 108px;
    text-align: left;
    border: 1px solid #e6eef4;
    background: #f8fbfd;
    border-radius: 10px;
    padding: 10px 14px;
    cursor: pointer;
    font-family: Poppins, sans-serif;
    transition: all 0.15s ease;
    box-sizing: border-box;
  }
  .rp-cluster-tab:hover { border-color: #c5d5e3; background: #fff; }
  .rp-cluster-tab--active {
    background: #032B4E; border-color: #032B4E; color: #fff;
    box-shadow: 0 4px 12px rgba(3, 43, 78, 0.18);
  }
  .rp-cluster-tab-name { display: block; font-size: 13px; font-weight: 700; }
  .rp-cluster-tab-meta { display: block; margin-top: 2px; font-size: 11px; opacity: 0.8; }
  .rp-summary-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    align-items: stretch;
    width: 100%;
  }
  .rp-nested {
    background: #f8fbfd;
    border: 1px solid #e6eef4;
    border-radius: 10px;
    padding: 12px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
  }
  .rp-card {
    background: #fff;
    border: 1px solid #e6eef4;
    border-radius: 12px;
    padding: 12px 14px 14px;
    box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
    box-sizing: border-box;
    width: 100%;
  }
  .rp-card-head {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .rp-card-head--parts {
    align-items: center;
    margin-bottom: 12px;
  }
  .rp-card-head--parts > div {
    min-width: 0;
    flex: 1 1 220px;
  }
  .rp-card-head h4 {
    margin: 0; font-size: 14px; font-weight: 700; color: #0c3c54;
    font-family: Poppins, sans-serif;
  }
  .rp-card-head p, .rp-card-head span {
    margin: 2px 0 0; font-size: 12px; color: #6a7d8c; font-family: Poppins, sans-serif;
  }
  .rp-parts-count {
    margin: 0 !important;
    padding: 4px 10px;
    border-radius: 999px;
    background: #f4f8fb;
    border: 1px solid #e6eef4;
    color: #032B4E !important;
    font-weight: 600;
    white-space: nowrap;
  }
  .rp-mini-table-wrap, .rp-parts-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #eef3f7;
    border-radius: 10px;
    min-width: 0;
    width: 100%;
    -webkit-overflow-scrolling: touch;
  }
  .rp-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-family: Poppins, sans-serif;
    font-size: 12px;
    table-layout: auto;
  }
  .rp-table--summary {
    min-width: 100%;
    width: max-content;
  }
  .rp-table--parts { min-width: 760px; }
  .rp-table th, .rp-table td {
    padding: 10px 12px; border-bottom: 1px solid #eef3f7; text-align: center;
    white-space: nowrap; vertical-align: middle;
  }
  .rp-table th {
    background: #032B4E; color: #fff; font-weight: 600; font-size: 11px;
  }
  .rp-th-main {
    display: block;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
  }
  .rp-th-sub {
    display: block;
    margin-top: 3px;
    font-size: 10px;
    font-weight: 500;
    opacity: 0.82;
    line-height: 1.2;
  }
  .rp-week-head {
    min-width: 118px;
    background: #1a4068 !important;
  }
  .rp-table tbody tr:nth-child(even) { background: #f8fbfd; }
  .rp-table tbody tr:hover { background: #fff7f2; }
  .rp-sticky-col {
    position: sticky; left: 0; background: inherit; z-index: 1;
    text-align: left !important; font-weight: 700; color: #0c3c54;
    min-width: 72px;
  }
  thead .rp-sticky-col { background: #032B4E; color: #fff; z-index: 2; }
  tbody tr:nth-child(even) .rp-sticky-col { background: #f8fbfd; }
  tbody tr:hover .rp-sticky-col { background: #fff7f2; }
  .rp-part-cell { font-weight: 700; color: #032B4E; }
  .rp-mfg-date {
    display: block;
    font-weight: 700;
    color: #0c3c54;
    font-size: 12px;
    line-height: 1.3;
  }
  .rp-mfg-label {
    display: block;
    margin-top: 4px;
    font-size: 10px;
    color: #E06639;
    font-weight: 600;
    line-height: 1.2;
  }
  .rp-table--summary td {
    vertical-align: middle;
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .rp-week-cell {
    min-width: 118px;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }
  .rp-cell-label {
    display: block;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #8a9bab;
    line-height: 1.2;
  }
  .rp-qty {
    display: block;
    font-weight: 700;
    color: #0c3c54;
    font-size: 13px;
    line-height: 1.2;
    margin: 1px 0 6px;
  }
  .rp-date {
    display: block;
    color: #368E60;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
  }
  .rp-empty-cell { text-align: center !important; color: #6a7d8c; padding: 18px !important; }
  .rp-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 12px;
    flex-wrap: wrap;
  }
  .rp-page-numbers { display: flex; gap: 4px; flex-wrap: wrap; }
  .rp-page-btn {
    min-width: 34px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid #e6eef4;
    border-radius: 8px;
    background: #fff;
    color: #032B4E;
    font-size: 12px;
    font-weight: 600;
    font-family: Poppins, sans-serif;
    cursor: pointer;
  }
  .rp-page-btn:hover:not(:disabled) {
    border-color: #032B4E;
    background: #f6fafd;
  }
  .rp-page-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .rp-page-btn--active {
    background: #032B4E;
    border-color: #032B4E;
    color: #fff;
  }
  .rp-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    background: #fff;
    border: 1px solid #e6eef4;
    border-radius: 12px;
    padding: 14px 16px;
    box-shadow: 0 3px 12px rgba(12, 60, 84, 0.06);
  }
  .rp-actions-copy {
    flex: 1 1 240px;
    min-width: 0;
  }
  .rp-status { margin: 0; font-size: 13px; color: #6a7d8c; font-family: Poppins, sans-serif; line-height: 1.45; }
  .rp-status--sent { color: #368E60; font-weight: 600; }
  .rp-primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    background: #E06639;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    font-family: Poppins, sans-serif;
    box-shadow: 0 2px 8px rgba(224, 102, 57, 0.28);
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 0.15s ease, box-shadow 0.15s ease;
  }
  .rp-primary-btn:hover:not(:disabled) { background: #c9552e; }
  .rp-primary-btn:disabled {
    opacity: 0.7; cursor: default; background: #368E60; box-shadow: none;
  }
  .rp-empty {
    margin: 0; padding: 24px; text-align: center; color: #6a7d8c;
    font-family: Poppins, sans-serif;
  }

  @media (max-width: 900px) {
    .rp-summary-grid { grid-template-columns: 1fr; }
    .scm-plan-edit-btn { margin-left: 0; align-self: flex-start; }
  }
  @media (max-width: 720px) {
    .scm-plan-status-row {
      flex-direction: column;
      align-items: stretch;
    }
    .scm-workflow-edit-actions {
      margin-left: 0;
      width: 100%;
      justify-content: flex-end;
    }
    .scm-workflow-select { max-width: none; }
    .rp-pagination { justify-content: center; }
    .rp-actions {
      flex-direction: column;
      align-items: stretch;
    }
    .rp-primary-btn {
      width: 100%;
    }
  }
`;
